// src/pages/OperatorSecurity.tsx
//
// Where an operator enrols their second factor and their passkeys.
//
// This page is what makes operator 2FA possible at all. Before it existed the
// portal was deadlocked: requireStepUp() refuses the destructive endpoints
// without an enrolled second factor, OperatorSignIn refused to complete a
// sign-in for an account that had none, and nothing anywhere called
// twoFactor.enable. The only reason anyone could still work was the
// isLegacyOperator() bypass, which exists solely because the operator was
// still on Zitadel.
//
// It deliberately sits OUTSIDE the 2FA gate in RequireAuth — gating enrolment
// on being enrolled is the deadlock itself.
//
// ORDER MATTERS: Better Auth returns the TOTP URI and the backup codes only
// from enable(), and the codes are shown exactly once. So enable() must not be
// called until the operator is actually in front of the screen ready to scan,
// and the codes must be rendered before verify() navigates away.

import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { twoFactor, passkey, passkeysReady, authClient } from '../auth/betterAuthClient'
import { useOperatorAuth } from '../context/OperatorAuthProvider'

type Stage = 'intro' | 'scan' | 'codes' | 'done'

interface PasskeyRow {
  id: string
  name?: string | null
  /** A Date, not a string — the plugin parses it before it reaches the client. */
  createdAt?: Date
}

export default function OperatorSecurity() {
  const navigate = useNavigate()
  const { twoFactorEnabled, refresh, user } = useOperatorAuth()

  const [stage, setStage] = useState<Stage>(twoFactorEnabled ? 'done' : 'intro')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [passkeys, setPasskeys] = useState<PasskeyRow[]>([])
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  const [passkeyMsg, setPasskeyMsg] = useState<string | null>(null)

  const loadPasskeys = useCallback(async () => {
    try {
      const res = await passkey.listUserPasskeys()
      if (!res.error && Array.isArray(res.data)) setPasskeys(res.data as PasskeyRow[])
    } catch {
      /* listing is informational; a failure here must not block enrolment */
    }
  }, [])

  useEffect(() => { void loadPasskeys() }, [loadPasskeys])

  /** Step 1 — Better Auth requires the current password to arm TOTP. */
  async function startEnrolment(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await twoFactor.enable({ password })
      if (res.error) {
        setError(res.error.message || 'Could not start enrolment. Check your password.')
        return
      }
      const data = res.data as { totpURI?: string; backupCodes?: string[] }
      if (!data?.totpURI) {
        setError('The server did not return an enrolment URI.')
        return
      }
      setSecret(new URL(data.totpURI).searchParams.get('secret'))
      setBackupCodes(data.backupCodes ?? [])
      setQrDataUrl(await QRCode.toDataURL(data.totpURI, { width: 200, margin: 1 }))
      setStage('scan')
    } catch {
      setError('Could not reach the API.')
    } finally {
      setBusy(false)
    }
  }

  /** Step 2 — prove the authenticator works before we call it enrolled. */
  async function verifyCode(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await twoFactor.verifyTotp({ code })
      if (res.error) {
        setError(res.error.message || 'That code was not accepted.')
        return
      }
      // Codes are shown AFTER verification so nobody saves a set for an
      // enrolment that never completed.
      setStage(backupCodes.length ? 'codes' : 'done')
      await refresh()
    } catch {
      setError('Could not verify the code.')
    } finally {
      setBusy(false)
    }
  }

  async function addPasskey() {
    setPasskeyBusy(true)
    setPasskeyMsg(null)
    try {
      const res = await passkey.addPasskey({ name: `${user?.email ?? 'operator'} — ${new Date().toLocaleDateString()}` })
      if (res?.error) {
        setPasskeyMsg(res.error.message || 'Could not register that passkey.')
        return
      }
      setPasskeyMsg('Passkey registered.')
      await loadPasskeys()
    } catch {
      // A user who dismisses the browser prompt lands here. That is a choice,
      // not a fault, so it does not read as an error.
      setPasskeyMsg('Registration was cancelled.')
    } finally {
      setPasskeyBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-10">
      <div className="mx-auto w-full max-w-2xl space-y-6">

        <header>
          <h1 className="text-xl font-semibold text-slate-900">Operator security</h1>
          <p className="mt-1 text-sm text-slate-600">
            This account reaches every tenant database. A second factor is required before
            impersonation, session revocation, database tools or migrations will run.
          </p>
        </header>

        {/* ── Two-factor ─────────────────────────────────────────── */}
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-slate-500" />
            <h2 className="font-semibold text-slate-900">Authenticator app</h2>
            {twoFactorEnabled && (
              <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                <CheckCircleIcon className="h-3.5 w-3.5" /> Enrolled
              </span>
            )}
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {stage === 'intro' && (
            <form onSubmit={startEnrolment} className="space-y-3">
              <p className="text-sm text-slate-600">
                Confirm your password to begin. You will scan a QR code with Google Authenticator,
                1Password, Authy or similar.
              </p>
              <div>
                <label htmlFor="cur-pw" className="mb-1 block text-xs font-semibold text-slate-700">
                  Current password
                </label>
                <input
                  id="cur-pw" type="password" value={password} autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)} required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <button
                type="submit" disabled={busy || !password}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Begin enrolment
              </button>
            </form>
          )}

          {stage === 'scan' && (
            <form onSubmit={verifyCode} className="space-y-4">
              <div className="flex flex-wrap items-start gap-5">
                {qrDataUrl && (
                  <img src={qrDataUrl} alt="Two-factor QR code" className="h-40 w-40 rounded-lg border border-slate-200" />
                )}
                <div className="min-w-[200px] flex-1">
                  <p className="text-sm text-slate-600">Cannot scan? Enter this key by hand:</p>
                  <code className="mt-2 block break-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-xs text-slate-800">
                    {secret}
                  </code>
                  <label htmlFor="totp-verify" className="mt-4 mb-1 block text-xs font-semibold text-slate-700">
                    Enter the 6-digit code it shows
                  </label>
                  <input
                    id="totp-verify" value={code} inputMode="numeric" maxLength={6} required
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center font-mono text-lg tracking-[0.3em]"
                  />
                </div>
              </div>
              <button
                type="submit" disabled={busy || code.length !== 6}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Turn on two-factor
              </button>
            </form>
          )}

          {stage === 'codes' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Save these backup codes. Each works once, if you lose your authenticator.
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {backupCodes.map((c) => (
                  <code key={c} className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-center font-mono text-xs">
                    {c}
                  </code>
                ))}
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <span>This is the only time these are shown.</span>
              </div>
              <button
                onClick={() => { setStage('done'); navigate('/', { replace: true }) }}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
              >
                I have saved them
              </button>
            </div>
          )}

          {stage === 'done' && (
            <p className="text-sm text-slate-600">
              Two-factor authentication is active on this account.
            </p>
          )}
        </section>

        {/* ── Passkeys ───────────────────────────────────────────── */}
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <KeyIcon className="h-5 w-5 text-slate-500" />
            <h2 className="font-semibold text-slate-900">Passkeys</h2>
          </div>

          {!passkeysReady() ? (
            // Registering here would produce credentials bound to a hostname
            // this portal is leaving. They cannot be migrated, and they fail
            // silently after the move — so the control is withheld rather than
            // offered with a warning nobody reads.
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Passkey registration is disabled on <code className="font-mono">{location.hostname}</code>.
                A passkey is permanently bound to the domain that created it, and this portal is
                still scheduled to move to admin.citstime.app — anything registered now would stop
                working there, with no way to migrate it. Enable once the move is done.
              </span>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Sign in with Touch ID, Windows Hello or a hardware key instead of a password.
              </p>
              {passkeys.length > 0 && (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {passkeys.map((p) => (
                    <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-slate-800">{p.name || 'Unnamed passkey'}</span>
                      <button
                        onClick={async () => { await passkey.deletePasskey({ id: p.id }); void loadPasskeys() }}
                        className="text-xs font-semibold text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {passkeyMsg && <p className="text-sm text-slate-600">{passkeyMsg}</p>}
              <button
                onClick={addPasskey} disabled={passkeyBusy}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 disabled:opacity-50"
              >
                {passkeyBusy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Add a passkey
              </button>
            </div>
          )}
        </section>

        <button
          onClick={() => authClient.signOut().then(() => location.assign('/signin'))}
          className="text-sm font-semibold text-slate-500 hover:text-slate-800"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
